import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  createManager,
  updateManager,
  getManagers,
  deleteManager
} from '../../../api/managerApi';
import {
  type ICreateManagerRequest,
  type IManager,
  type IUpdateManagerRequest,
  type IUpdateManagerResponse,
  type IGetManagersRequest,
  type IGetManagersResponse,
  type IDeleteManagerResponse
} from '../../../types/IManager';
import { getErrorData, type IErrorData } from '../../../utils/getErrorData';

/**
 * Thunk для создания менеджера
 */
export const createManagerThunk = createAsyncThunk<
  IManager,
  ICreateManagerRequest,
  { rejectValue: IErrorData }
>('managersAccesses/createManager', async (data, { rejectWithValue }) => {
  try {
    const response = await createManager(data);

    if (response.response === null) {
      return rejectWithValue({ message: 'Некорректный ответ сервера' });
    }

    return response.response;
  } catch (error) {
    const extractedError = getErrorData(error);
    return rejectWithValue(extractedError);
  }
});

export interface IUpdateManagerThunkOptions {
  managerUuid: string;
  managerId: number;
  data: IUpdateManagerRequest;
}

/**
 * Thunk для обновления менеджера
 */
export const updateManagerThunk = createAsyncThunk<
  IUpdateManagerResponse,
  IUpdateManagerThunkOptions,
  { rejectValue: IErrorData }
>(
  'managersAccesses/updateManager',
  async ({ managerUuid, managerId, data }, { rejectWithValue }) => {
    try {
      const response = await updateManager(managerUuid, data);

      if (response.response === null) {
        return rejectWithValue({ message: 'Некорректный ответ сервера' });
      }

      return { ...response.response, managerId };
    } catch (error) {
      const extractedError = getErrorData(error);
      return rejectWithValue(extractedError);
    }
  }
);

/**
 * Thunk для получения списка менеджеров
 */
export const getManagersThunk = createAsyncThunk<
  IGetManagersResponse,
  IGetManagersRequest | undefined,
  { rejectValue: IErrorData }
>('managersAccesses/getManagers', async (params, { rejectWithValue }) => {
  try {
    const response = await getManagers(params);

    if (response.response === null) {
      return rejectWithValue({ message: 'Некорректный ответ сервера' });
    }

    return response.response;
  } catch (error) {
    const extractedError = getErrorData(error);
    return rejectWithValue(extractedError);
  }
});

/**
 * Thunk для "тихого" обновления списка менеджеров (без показа общего лоадера)
 * Используется при обновлении после редактирования конкретного менеджера
 */
export const getManagersSilentThunk = createAsyncThunk<
  IGetManagersResponse,
  IGetManagersRequest | undefined,
  { rejectValue: IErrorData }
>('managersAccesses/getManagersSilent', async (params, { rejectWithValue }) => {
  try {
    const response = await getManagers(params);

    if (response.response === null) {
      return rejectWithValue({ message: 'Некорректный ответ сервера' });
    }

    return response.response;
  } catch (error) {
    const extractedError = getErrorData(error);
    return rejectWithValue(extractedError);
  }
});

/**
 * Thunk для удаления менеджера
 */
export const deleteManagerThunk = createAsyncThunk<
  IDeleteManagerResponse,
  string,
  { rejectValue: IErrorData }
>(
  'managersAccesses/deleteManager',
  async (managerUuid, { rejectWithValue }) => {
    try {
      const response = await deleteManager(managerUuid);

      if (response.response === null) {
        return rejectWithValue({ message: 'Некорректный ответ сервера' });
      }

      return response.response;
    } catch (error) {
      const extractedError = getErrorData(error);
      return rejectWithValue(extractedError);
    }
  }
);
